import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import Hls from 'hls.js';
import {
  Typography,
  Box,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Fullscreen as FullscreenIcon,
  Replay10 as Replay10Icon,
  Forward10 as Forward10Icon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Speed as SpeedIcon,
  Close as CloseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';
import {
  authenticatedFetch,
  downloadRecordingClip,
  getCameraStreams,
  hlsStreamLoaderOptions,
  isRecordingHlsStreamable,
  isRecordingWebPlayable,
  recordingTranscodingBlockedTooltip,
} from '../utils/api';
import { getOrFetch as fetchAuditImage } from '../services/auditImageCache';
import { buildApiUrl } from '../config/api';
import { recordingIsInOrOutLane } from './RecordingDayTimeline';
import { RecordingDownloadPanel } from './RecordingDownloadPanel';

const HLS_MAX_BUFFER_SECONDS = 15;
const HLS_MAX_MAX_BUFFER_SECONDS = 30;
const HLS_BACK_BUFFER_SECONDS = 10;
const RECORDING_CLICK_GUARD_UNTIL_KEY = '__recordingClickGuardUntil';
const RECORDING_CLICK_GUARD_WINDOW_MS = 350;

const markRecordingClickGuard = () => {
  if (typeof window === 'undefined') return;
  window[RECORDING_CLICK_GUARD_UNTIL_KEY] = Date.now() + RECORDING_CLICK_GUARD_WINDOW_MS;
};

const formatRecordedDate = (isoString) => {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoString;
  }
};

const formatDurationSeconds = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Math.floor(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

const VIDEO_TIME_ONE_HOUR_SEC = 3600;

/**
 * Scrubber-style time: MM:SS under 1h; H:MM:SS when the clip is ≥1h (both sides stay aligned).
 * @param {number|null|undefined} seconds
 * @param {{ useExtendedFormat?: boolean }} [opts]
 */
const formatVideoTime = (seconds, opts = {}) => {
  const raw = seconds == null || Number.isNaN(Number(seconds)) ? 0 : Math.floor(Number(seconds));
  const s = Math.max(0, raw);
  const pad2 = (n) => String(n).padStart(2, '0');
  if (opts.useExtendedFormat) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${pad2(m)}:${pad2(sec)}`;
  }
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${pad2(m)}:${pad2(sec)}`;
};

const parseRecordingStartMs = (isoString) => {
  if (!isoString || typeof isoString !== 'string' || !isoString.trim()) return null;
  const ms = Date.parse(isoString);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Recording wall clock: start timestamp + offset, 12-hour with seconds (e.g. 1:02:03 PM).
 * @param {string|null|undefined} isoStart
 * @param {number|null|undefined} offsetSeconds
 * @returns {string|null}
 */
const formatWallClockFromRecordingOffset = (isoStart, offsetSeconds) => {
  const baseMs = parseRecordingStartMs(isoStart);
  if (baseMs == null) return null;
  const off = offsetSeconds == null || Number.isNaN(Number(offsetSeconds)) ? 0 : Number(offsetSeconds);
  const d = new Date(baseMs + Math.max(0, off) * 1000);
  try {
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return null;
  }
};

/**
 * Move the player to the event offset.
 * HLS often reports a short duration (first fragment) before the playlist length
 * is known. Clamping to that and forgetting the seek leaves playback at the start.
 * @returns {boolean} true once the seek is applied against a long-enough duration
 */
const applyPendingPlaybackSeek = (video, pendingRef, expectedDuration = 0) => {
  if (!video || !pendingRef) return false;
  const seek = pendingRef.current;
  if (seek == null || !Number.isFinite(seek)) return false;
  const duration = video.duration;
  const durationKnown = Number.isFinite(duration) && duration > 0.25;
  const expected = Number(expectedDuration);
  const expectedReady = Number.isFinite(expected) && expected > 1 && durationKnown && duration + 2 >= expected;
  // Wait while HLS has only published the first fragment.
  if (durationKnown && seek > duration + 0.25 && !expectedReady) return false;
  const target = durationKnown
    ? Math.max(0, Math.min(seek, Math.max(0, duration - 0.05)))
    : Math.max(0, seek);
  try {
    if (Math.abs((video.currentTime || 0) - target) > 0.35) {
      video.currentTime = target;
    }
  } catch (_) {
    return false;
  }
  if (!durationKnown && !expectedReady) return false;
  pendingRef.current = null;
  return true;
};

const extractStreamTokenFromPayload = (data) => {
  if (data?.token) return data.token;
  const url = data?.url || '';
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return null;
  return new URLSearchParams(url.slice(qIndex + 1)).get('token');
};

/**
 * Full-featured recording player (same UI as Recordings page): custom controls, attendance
 * timeline dots, HLS/MKV, speed menu, MKV clip export with range selection.
 *
 * @param {boolean} open
 * @param {object|null} recording - Row from recordings API
 * @param {() => void} onClose
 * @param {number|null} [initialSeekSeconds] - Seek after load; null skips
 * @param {number} [playbackSessionId] - Bump when reopening same id with new seek
 * @param {object[]|null|undefined} [configuredCameras] - From `/camera-streams` (with `camera_type`). When set, avoids an extra fetch for IN/OUT lane detection.
 */
export function RecordingPlaybackModal({
  open,
  recording,
  onClose,
  onPlaybackUnavailable = null,
  initialSeekSeconds = null,
  playbackSessionId = 0,
  configuredCameras = undefined,
}) {
  const onPlaybackUnavailableRef = useRef(onPlaybackUnavailable);
  const onCloseRef = useRef(onClose);
  onPlaybackUnavailableRef.current = onPlaybackUnavailable;
  onCloseRef.current = onClose;
  const [videoObjectUrl, setVideoObjectUrl] = useState(null);
  const [useHlsPlayback, setUseHlsPlayback] = useState(false);
  const [hlsManifestAbsoluteUrl, setHlsManifestAbsoluteUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [seekBuffering, setSeekBuffering] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showCenterControls, setShowCenterControls] = useState(false);
  const [dotsMenuAnchor, setDotsMenuAnchor] = useState(null);
  const [speedSubmenuOpen, setSpeedSubmenuOpen] = useState(false);
  const [previewXPercent, setPreviewXPercent] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [attendanceLogsLoading, setAttendanceLogsLoading] = useState(false);
  const [hoveredAttendanceLog, setHoveredAttendanceLog] = useState(null);
  const [attendanceLogPreviewImage, setAttendanceLogPreviewImage] = useState(null);
  const [attendancePreviewStatus, setAttendancePreviewStatus] = useState('idle');
  const [hiddenActions, setHiddenActions] = useState(new Set());
  const [clipStartSeconds, setClipStartSeconds] = useState(0);
  const [clipEndSeconds, setClipEndSeconds] = useState(0);
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [playbackMuted, setPlaybackMuted] = useState(true);

  const videoRef = useRef(null);
  const hlsStreamTokenRef = useRef('');
  const pendingPlaybackSeekRef = useRef(null);
  const videoAutoplayAttemptedRef = useRef(false);
  const timelineHoverTimeoutRef = useRef(null);
  const attendanceTimelineRef = useRef(null);
  const centerControlsTimeoutRef = useRef(null);
  const playbackMutedRef = useRef(true);
  const [laneConfigCameras, setLaneConfigCameras] = useState([]);

  const applyPlaybackAudio = useCallback((video, muted) => {
    if (!video) return;
    video.muted = muted;
    video.volume = muted ? 0 : 1;
  }, []);

  const togglePlaybackMute = useCallback(() => {
    setPlaybackMuted((prev) => {
      const next = !prev;
      playbackMutedRef.current = next;
      const video = videoRef.current;
      if (video) {
        applyPlaybackAudio(video, next);
        // Unmute only toggles audio — do not remount HLS / trigger buffering UI.
        if (!next && video.paused) {
          video.play().catch(() => {});
        }
      }
      return next;
    });
  }, [applyPlaybackAudio]);

  useEffect(() => {
    if (!open) {
      playbackMutedRef.current = true;
      setPlaybackMuted(true);
    }
  }, [open]);

  // Keep DOM muted/volume in sync without remounting HLS (refs used in player setup).
  useEffect(() => {
    playbackMutedRef.current = playbackMuted;
    if (!open || videoLoading) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    applyPlaybackAudio(video, playbackMuted);
    return undefined;
  }, [open, videoLoading, playbackMuted, applyPlaybackAudio]);

  useEffect(() => {
    if (!open) return;
    if (Array.isArray(configuredCameras)) {
      setLaneConfigCameras(configuredCameras);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const payload = await getCameraStreams();
        if (!cancelled) setLaneConfigCameras(Array.isArray(payload?.items) ? payload.items : []);
      } catch {
        if (!cancelled) setLaneConfigCameras([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, configuredCameras]);

  const showAttendanceTimeline = useMemo(() => {
    if (!recording) return false;
    return recordingIsInOrOutLane(recording, laneConfigCameras);
  }, [recording, laneConfigCameras]);

  useEffect(() => {
    if (!showAttendanceTimeline) {
      setHoveredAttendanceLog(null);
      setAttendanceLogPreviewImage(null);
    }
  }, [showAttendanceTimeline]);

  const resetPlayerUiState = useCallback(() => {
    setVideoObjectUrl(null);
    setUseHlsPlayback(false);
    setHlsManifestAbsoluteUrl(null);
    setVideoLoading(false);
    setSeekBuffering(false);
    setVideoError(null);
    setPreviewVisible(false);
    setIsPlaying(false);
    setPlaybackSpeed(1);
    setShowCenterControls(false);
    setDotsMenuAnchor(null);
    setSpeedSubmenuOpen(false);
    setPreviewXPercent(0);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setAttendanceLogs([]);
    setHoveredAttendanceLog(null);
    setAttendanceLogPreviewImage(null);
    setHiddenActions(new Set());
    setClipStartSeconds(0);
    setClipEndSeconds(0);
    setExportPanelOpen(false);
    pendingPlaybackSeekRef.current = null;
    videoAutoplayAttemptedRef.current = false;
    if (timelineHoverTimeoutRef.current) {
      clearTimeout(timelineHoverTimeoutRef.current);
      timelineHoverTimeoutRef.current = null;
    }
    if (centerControlsTimeoutRef.current) {
      clearTimeout(centerControlsTimeoutRef.current);
      centerControlsTimeoutRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    markRecordingClickGuard();
    if (videoObjectUrl && videoObjectUrl.startsWith('blob:')) {
      URL.revokeObjectURL(videoObjectUrl);
    }
    resetPlayerUiState();
    onClose();
  }, [videoObjectUrl, onClose, resetPlayerUiState]);

  useEffect(() => {
    if (!open || !recording?.id) return undefined;
    let cancelled = false;
    const rec = recording;

    pendingPlaybackSeekRef.current =
      initialSeekSeconds != null &&
      typeof initialSeekSeconds === 'number' &&
      Number.isFinite(initialSeekSeconds) &&
      initialSeekSeconds >= 0
        ? initialSeekSeconds
        : null;
    videoAutoplayAttemptedRef.current = false;
    setPlaybackSpeed(1);
    setVideoError(null);
    setVideoLoading(true);
    setVideoObjectUrl(null);
    setUseHlsPlayback(false);
    setHlsManifestAbsoluteUrl(null);

    const fetchStreamUrl = async () => {
      try {
        if (!isRecordingWebPlayable(rec)) {
          throw new Error(
            recordingTranscodingBlockedTooltip(rec) || 'Playback unavailable for this recording.',
          );
        }
        const res = await authenticatedFetch(
          `/recording/recordings/${encodeURIComponent(rec.id)}/stream-url`
        );
        const data = await res.json().catch(() => {
          throw new Error('Invalid stream URL response');
        });
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid stream URL payload');
        }
        if (cancelled) return;

        const streamToken = extractStreamTokenFromPayload(data);
        if (!streamToken) {
          throw new Error('Missing playback token');
        }
        hlsStreamTokenRef.current = streamToken;

        if (isRecordingHlsStreamable(rec)) {
          setUseHlsPlayback(true);
          setVideoObjectUrl(null);
          const manifestRel = `/recording/recordings/${encodeURIComponent(rec.id)}/hls/index.m3u8`;
          setHlsManifestAbsoluteUrl(buildApiUrl(manifestRel));
        } else {
          setUseHlsPlayback(false);
          setHlsManifestAbsoluteUrl(null);
          setVideoObjectUrl(buildApiUrl(data.url));
        }
      } catch (e) {
        if (cancelled) return;
        const message = e?.message || 'Failed to load video';
        const transcoding = /transcoding in progress/i.test(message);
        if (transcoding && typeof onPlaybackUnavailableRef.current === 'function') {
          onPlaybackUnavailableRef.current();
          onCloseRef.current?.();
          return;
        }
        setVideoError(message);
      } finally {
        if (!cancelled) setVideoLoading(false);
      }
    };
    fetchStreamUrl();
    return () => {
      cancelled = true;
    };
    // Intentionally omit `recording` object identity — only id/session/seek should refetch stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recording?.id + session covers row changes
  }, [open, recording?.id, playbackSessionId, initialSeekSeconds]);

  useEffect(() => {
    setExportPanelOpen(false);
  }, [recording?.id, playbackSessionId]);

  useEffect(() => {
    if (!open || videoLoading || pendingPlaybackSeekRef.current == null) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    const trySeek = () => {
      if (pendingPlaybackSeekRef.current == null) return;
      const applied = applyPendingPlaybackSeek(
        video,
        pendingPlaybackSeekRef,
        recording?.duration_seconds,
      );
      if (!applied) return;
      applyPlaybackAudio(video, playbackMutedRef.current);
      video.play().catch(() => {});
    };
    trySeek();
    if (pendingPlaybackSeekRef.current == null) return undefined;
    video.addEventListener('loadedmetadata', trySeek);
    video.addEventListener('durationchange', trySeek);
    return () => {
      video.removeEventListener('loadedmetadata', trySeek);
      video.removeEventListener('durationchange', trySeek);
    };
  }, [
    open,
    videoLoading,
    playbackSessionId,
    recording?.id,
    recording?.duration_seconds,
    useHlsPlayback,
    videoObjectUrl,
    hlsManifestAbsoluteUrl,
    applyPlaybackAudio,
  ]);

  useLayoutEffect(() => {
    if (!open || videoLoading || !useHlsPlayback || !hlsManifestAbsoluteUrl) {
      return undefined;
    }

    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    applyPlaybackAudio(video, playbackMutedRef.current);

    let cancelled = false;
    let hls = null;

    const onFatalHlsError = (details) => {
      if (cancelled) return;
      setVideoError(details || 'HLS playback failed');
    };

    const startAt = pendingPlaybackSeekRef.current;
    if (Hls.isSupported()) {
      hls = new Hls({
        ...hlsStreamLoaderOptions(hlsStreamTokenRef.current),
        lowLatencyMode: false,
        maxBufferLength: HLS_MAX_BUFFER_SECONDS,
        maxMaxBufferLength: HLS_MAX_MAX_BUFFER_SECONDS,
        backBufferLength: HLS_BACK_BUFFER_SECONDS,
        startFragPrefetch: true,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeMaxRetry: 5,
        startPosition: startAt != null && Number.isFinite(startAt) ? startAt : -1,
      });
      hls.loadSource(hlsManifestAbsoluteUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (cancelled) return;
        applyPendingPlaybackSeek(video, pendingPlaybackSeekRef, recording?.duration_seconds);
        applyPlaybackAudio(video, playbackMutedRef.current);
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (cancelled || !data) return;
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          try {
            hls.startLoad();
          } catch (_) {
            onFatalHlsError(data.details);
          }
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          try {
            hls.recoverMediaError();
          } catch (_) {
            onFatalHlsError(data.details);
          }
          return;
        }
        onFatalHlsError(data.details);
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsManifestAbsoluteUrl;
      const onMeta = () => {
        if (cancelled) return;
        applyPendingPlaybackSeek(video, pendingPlaybackSeekRef, recording?.duration_seconds);
        applyPlaybackAudio(video, playbackMutedRef.current);
        video.play().catch(() => {});
      };
      video.addEventListener('loadedmetadata', onMeta, { once: true });
    } else {
      setVideoError('HLS is not supported in this browser');
    }

    return () => {
      cancelled = true;
      if (hls) {
        try {
          hls.destroy();
        } catch (_) {
          /* ignore */
        }
      }
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (_) {
        /* ignore */
      }
    };
    // Intentionally omit playbackMuted — mute must not destroy/rebuild HLS.
  }, [open, videoLoading, useHlsPlayback, hlsManifestAbsoluteUrl, recording?.id, recording?.duration_seconds, applyPlaybackAudio]);

  useEffect(() => {
    if (!open || !recording?.id) return undefined;
    if (!recordingIsInOrOutLane(recording, laneConfigCameras)) {
      setAttendanceLogs([]);
      setAttendanceLogsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fetchAttendanceLogs = async () => {
      setAttendanceLogsLoading(true);
      try {
        const res = await authenticatedFetch(
          `/recording/recordings/${encodeURIComponent(recording.id)}/attendance-logs`
        );
        if (!res.ok) throw new Error('Failed to load attendance logs');
        const data = await res.json();
        if (!cancelled) setAttendanceLogs(data.logs || []);
      } catch (_) {
        if (!cancelled) setAttendanceLogs([]);
      } finally {
        if (!cancelled) setAttendanceLogsLoading(false);
      }
    };
    fetchAttendanceLogs();
    return () => {
      cancelled = true;
    };
  }, [open, recording?.id, recording, laneConfigCameras]);

  const handleVideoAreaMouseLeave = useCallback(() => {
    if (timelineHoverTimeoutRef.current) {
      clearTimeout(timelineHoverTimeoutRef.current);
      timelineHoverTimeoutRef.current = null;
    }
    if (centerControlsTimeoutRef.current) {
      clearTimeout(centerControlsTimeoutRef.current);
      centerControlsTimeoutRef.current = null;
    }
    setShowCenterControls(false);
    setPreviewVisible(false);
  }, []);

  const handleVideoTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || seekBuffering) return;
    setVideoCurrentTime(video.currentTime);
  }, [seekBuffering]);

  const seekToTime = useCallback(
    (t) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(t)) return;
      const d = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : videoDuration;
      const clamped = d > 0 ? Math.max(0, Math.min(t, d)) : Math.max(0, t);
      setVideoCurrentTime(clamped);
      setSeekBuffering(true);
      video.currentTime = clamped;
    },
    [videoDuration]
  );

  const handleVideoLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      const d = video.duration || 0;
      setVideoDuration(d);
      if (d > 0) {
        setClipEndSeconds((prev) => (prev <= 0 ? d : Math.min(prev, d)));
      }
    }
  }, []);

  useEffect(() => {
    const d =
      Number.isFinite(videoDuration) && videoDuration > 0
        ? videoDuration
        : recording?.duration_seconds != null &&
            Number.isFinite(Number(recording.duration_seconds))
          ? Number(recording.duration_seconds)
          : 0;
    if (d > 0) {
      setClipEndSeconds((prev) => (prev <= 0 || prev > d ? d : prev));
      setClipStartSeconds((prev) => Math.min(prev, Math.max(0, d - 0.1)));
    }
  }, [videoDuration, recording?.id, recording?.duration_seconds]);

  const handleVideoDirectUrlCanPlay = useCallback((e) => {
    const video = e.currentTarget;
    if (pendingPlaybackSeekRef.current != null) {
      const applied = applyPendingPlaybackSeek(
        video,
        pendingPlaybackSeekRef,
        recording?.duration_seconds,
      );
      if (!applied) return;
    }
    if (videoAutoplayAttemptedRef.current) return;
    videoAutoplayAttemptedRef.current = true;
    applyPlaybackAudio(video, playbackMutedRef.current);
    video.play().catch(() => {
      videoAutoplayAttemptedRef.current = false;
    });
  }, [applyPlaybackAudio, recording?.duration_seconds]);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    setSeekBuffering(false);
    const video = videoRef.current;
    if (video) setVideoCurrentTime(video.currentTime);
  }, []);
  const handleVideoPause = useCallback(() => setIsPlaying(false), []);

  const handleVideoSeeking = useCallback(() => {
    setSeekBuffering(true);
  }, []);

  const handleVideoWaiting = useCallback(() => {
    setSeekBuffering(true);
  }, []);

  const handleVideoSeeked = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setVideoCurrentTime(video.currentTime);
    if (video.paused || video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setSeekBuffering(false);
    }
  }, []);

  const handleFullscreen = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.requestFullscreen) video.requestFullscreen().catch(() => {});
    else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
  }, []);

  const handleSkipBackward = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    seekToTime(video.currentTime - 10);
  }, [seekToTime]);

  const handleSkipForward = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    seekToTime(video.currentTime + 10);
  }, [seekToTime]);

  const handleSpeedChange = useCallback((speed) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = speed;
    setPlaybackSpeed(speed);
    setSpeedSubmenuOpen(false);
    setDotsMenuAnchor(null);
  }, []);

  const flashCenterControls = useCallback(() => {
    setShowCenterControls(true);
    if (centerControlsTimeoutRef.current) clearTimeout(centerControlsTimeoutRef.current);
    centerControlsTimeoutRef.current = setTimeout(() => {
      setShowCenterControls(false);
    }, 1800);
  }, []);

  const handleVideoAreaMouseMove = useCallback(() => {
    setShowCenterControls(true);
    if (centerControlsTimeoutRef.current) clearTimeout(centerControlsTimeoutRef.current);
    centerControlsTimeoutRef.current = setTimeout(() => {
      setShowCenterControls(false);
    }, 2500);
  }, []);

  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    flashCenterControls();
  }, [flashCenterControls]);

  const hoveredAuditId = hoveredAttendanceLog?.attendance_id
    ? String(hoveredAttendanceLog.attendance_id).trim()
    : '';

  useEffect(() => {
    if (!previewVisible || !hoveredAuditId) {
      setAttendanceLogPreviewImage(null);
      setAttendancePreviewStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setAttendancePreviewStatus('loading');
    setAttendanceLogPreviewImage(null);
    fetchAuditImage(hoveredAuditId).then((url) => {
      if (cancelled) return;
      if (url) {
        setAttendanceLogPreviewImage(url);
        setAttendancePreviewStatus('ready');
      } else {
        setAttendanceLogPreviewImage(null);
        setAttendancePreviewStatus('missing');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [previewVisible, hoveredAuditId]);

  const DOT_SNAP_PIXELS = 12;

  const handleAttendanceTimelineMouseMove = useCallback(
    (e) => {
      const bar = attendanceTimelineRef.current;
      const video = videoRef.current;
      if (!bar || !video || !videoDuration) return;

      const rect = bar.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const time = percent * videoDuration;

      setPreviewXPercent(percent);

      const secondsPerPixel = videoDuration / rect.width;
      const snapThreshold = DOT_SNAP_PIXELS * secondsPerPixel;

      let nearbyLog = null;
      let minDist = Infinity;
      for (const log of attendanceLogs) {
        const dist = Math.abs(log.offset_seconds - time);
        if (dist <= snapThreshold && dist < minDist) {
          nearbyLog = log;
          minDist = dist;
        }
      }

      if (nearbyLog) {
        if (timelineHoverTimeoutRef.current) clearTimeout(timelineHoverTimeoutRef.current);
        setHoveredAttendanceLog(nearbyLog);
        setPreviewVisible(true);
      } else {
        setHoveredAttendanceLog(null);
        setPreviewVisible(false);
        if (timelineHoverTimeoutRef.current) clearTimeout(timelineHoverTimeoutRef.current);
      }
    },
    [videoDuration, attendanceLogs]
  );

  const handleAttendanceTimelineMouseLeave = useCallback(() => {
    if (timelineHoverTimeoutRef.current) {
      clearTimeout(timelineHoverTimeoutRef.current);
      timelineHoverTimeoutRef.current = null;
    }
    setPreviewVisible(false);
    setHoveredAttendanceLog(null);
  }, []);

  const handleAttendanceTimelineClick = useCallback(
    (e) => {
      const bar = attendanceTimelineRef.current;
      const video = videoRef.current;
      if (!bar || !video || !videoDuration) return;

      if (hoveredAttendanceLog != null && typeof hoveredAttendanceLog.offset_seconds === 'number') {
        seekToTime(Math.max(0, Math.min(videoDuration, hoveredAttendanceLog.offset_seconds)));
      } else {
        const rect = bar.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        seekToTime(percent * videoDuration);
      }
      setPreviewVisible(false);
    },
    [videoDuration, hoveredAttendanceLog, seekToTime]
  );

  const handleExportClip = useCallback(
    async (startSec, endSec) => {
      if (!recording?.id) return;
      await downloadRecordingClip(recording.id, {
        startSeconds: startSec,
        endSeconds: endSec,
      });
    },
    [recording?.id],
  );

  const selectedRecording = recording;

  const recordingStartIsoForWallClock =
    selectedRecording?.start_time || selectedRecording?.recorded_at;
  const scrubberTimelineEndSeconds =
    Number.isFinite(videoDuration) && videoDuration > 0
      ? videoDuration
      : selectedRecording?.duration_seconds != null &&
          Number.isFinite(Number(selectedRecording.duration_seconds))
        ? Number(selectedRecording.duration_seconds)
        : 0;
  const scrubberWallCurrent = formatWallClockFromRecordingOffset(
    recordingStartIsoForWallClock,
    videoCurrentTime
  );
  const scrubberWallEnd = formatWallClockFromRecordingOffset(
    recordingStartIsoForWallClock,
    scrubberTimelineEndSeconds
  );
  const scrubberShowWallClock = scrubberWallCurrent != null && scrubberWallEnd != null;

  return (
    <Dialog
      open={open}
      onClose={(event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        markRecordingClickGuard();
        handleClose();
      }}
      maxWidth={exportPanelOpen ? 'xl' : 'lg'}
      fullWidth
      PaperProps={{
        onMouseDown: (event) => {
          event.stopPropagation();
        },
        onClick: (event) => {
          event.stopPropagation();
        },
        sx: { borderRadius: 3 },
      }}
    >
      {selectedRecording && (
        <>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              pb: 2,
              mb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1, pr: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatRecordedDate(selectedRecording.start_time || selectedRecording.recorded_at)}
                {selectedRecording.end_time && ` — ${formatRecordedDate(selectedRecording.end_time)}`}
                {selectedRecording.duration_seconds != null &&
                  ` · ${formatDurationSeconds(selectedRecording.duration_seconds)}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {selectedRecording.filename}
              </Typography>
            </Stack>
            <IconButton
              aria-label="Close recording player"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                markRecordingClickGuard();
                handleClose();
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                markRecordingClickGuard();
              }}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                markRecordingClickGuard();
              }}
              size="small"
              sx={{
                color: 'text.secondary',
                mt: -0.5,
                mr: -0.5,
                '&:hover': { bgcolor: 'grey.200', color: 'text.primary' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {videoError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {videoError}
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ flex: exportPanelOpen ? 1 : undefined, minWidth: 0, width: '100%' }}>
            {(videoLoading || videoObjectUrl || (useHlsPlayback && hlsManifestAbsoluteUrl)) && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: exportPanelOpen ? { xs: '50vh', md: '58vh' } : '65vh',
                  minHeight: exportPanelOpen ? { xs: 280, md: 360 } : '65vh',
                  bgcolor: '#000',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  userSelect: 'none',
                }}
                onMouseLeave={handleVideoAreaMouseLeave}
                onMouseMove={handleVideoAreaMouseMove}
              >
                {videoLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 8,
                      bgcolor: '#000',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                {!videoLoading && (videoObjectUrl || (useHlsPlayback && hlsManifestAbsoluteUrl)) && (
                  <>
                    <Box
                      component="video"
                      key={`${selectedRecording.id}-${playbackSessionId}`}
                      ref={videoRef}
                      muted={playbackMuted}
                      onTimeUpdate={handleVideoTimeUpdate}
                      onLoadedMetadata={handleVideoLoadedMetadata}
                      onCanPlay={useHlsPlayback ? undefined : handleVideoDirectUrlCanPlay}
                      onSeeking={handleVideoSeeking}
                      onSeeked={handleVideoSeeked}
                      onWaiting={handleVideoWaiting}
                      onPlay={handleVideoPlay}
                      onPause={handleVideoPause}
                      onClick={handlePlayPause}
                      onCanPlay={(e) => {
                        setSeekBuffering(false);
                        if (!useHlsPlayback) {
                          handleVideoDirectUrlCanPlay(e);
                        }
                      }}
                      preload="metadata"
                      playsInline
                      sx={{
                        width: '100%',
                        height: '100%',
                        maxHeight: exportPanelOpen ? '100%' : '65vh',
                        objectFit: 'contain',
                        display: 'block',
                        cursor: 'pointer',
                      }}
                      src={useHlsPlayback ? undefined : videoObjectUrl}
                    />

                    {seekBuffering && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 5,
                          pointerEvents: 'none',
                          bgcolor: 'transparent',
                          pb: 8,
                        }}
                      >
                        <CircularProgress size={36} sx={{ color: 'rgba(255,255,255,0.85)' }} />
                      </Box>
                    )}

                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        opacity: showCenterControls ? 1 : 0,
                        transition: 'opacity 0.25s ease',
                        pointerEvents: 'none',
                        zIndex: 6,
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSkipBackward();
                          flashCenterControls();
                        }}
                        sx={{
                          pointerEvents: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          color: '#fff',
                          '&:hover .skip-bg': { opacity: 1 },
                        }}
                      >
                        <Box
                          className="skip-bg"
                          sx={{
                            position: 'relative',
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,0.45)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.75,
                            transition: 'opacity 0.15s',
                            backdropFilter: 'blur(4px)',
                            border: '1.5px solid rgba(255,255,255,0.18)',
                          }}
                        >
                          <Replay10Icon sx={{ fontSize: 30, color: '#fff' }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.67rem', letterSpacing: 0.4 }}>
                          10 sec
                        </Typography>
                      </Box>

                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayPause();
                        }}
                        sx={{
                          pointerEvents: 'auto',
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          bgcolor: 'rgba(0,0,0,0.55)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          backdropFilter: 'blur(6px)',
                          border: '1.5px solid rgba(255,255,255,0.22)',
                          transition: 'transform 0.12s ease, opacity 0.12s ease',
                          '&:hover': { transform: 'scale(1.1)', opacity: 0.9 },
                        }}
                      >
                        {isPlaying ? (
                          <PauseIcon sx={{ fontSize: 36, color: '#fff' }} />
                        ) : (
                          <PlayArrowIcon sx={{ fontSize: 36, color: '#fff' }} />
                        )}
                      </Box>

                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSkipForward();
                          flashCenterControls();
                        }}
                        sx={{
                          pointerEvents: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          color: '#fff',
                          '&:hover .skip-bg': { opacity: 1 },
                        }}
                      >
                        <Box
                          className="skip-bg"
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,0.45)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.75,
                            transition: 'opacity 0.15s',
                            backdropFilter: 'blur(4px)',
                            border: '1.5px solid rgba(255,255,255,0.18)',
                          }}
                        >
                          <Forward10Icon sx={{ fontSize: 30, color: '#fff' }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.67rem', letterSpacing: 0.4 }}>
                          10 sec
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 7,
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 55%, transparent 100%)',
                        px: 1.5,
                        pt: 4,
                        pb: 1,
                        opacity: showCenterControls ? 1 : 0.85,
                        transition: 'opacity 0.25s ease',
                      }}
                    >
                      {showAttendanceTimeline && attendanceLogs.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.55)',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                            }}
                          >
                            {attendanceLogs.length} event{attendanceLogs.length !== 1 ? 's' : ''}
                          </Typography>
                          {[
                            { label: 'Check In', action: 'check_in', color: '#4caf50' },
                            { label: 'Check Out', action: 'check_out', color: '#f44336' },
                          ].map(({ label, action, color }) => {
                            const isHidden = hiddenActions.has(action);
                            return (
                              <Box
                                key={label}
                                onClick={() =>
                                  setHiddenActions((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(action)) next.delete(action);
                                    else next.add(action);
                                    return next;
                                  })
                                }
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: 'pointer',
                                  opacity: isHidden ? 0.45 : 1,
                                  transition: 'opacity 0.2s',
                                  userSelect: 'none',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    bgcolor: isHidden ? '#888' : color,
                                    transition: 'background-color 0.2s',
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.65rem' }}>
                                  {label}
                                </Typography>
                              </Box>
                            );
                          })}
                          {attendanceLogsLoading && (
                            <CircularProgress size={10} sx={{ color: 'rgba(255,255,255,0.4)' }} />
                          )}
                        </Box>
                      )}

                      <Box sx={{ position: 'relative', mb: 0.75 }}>
                        <Box
                          ref={attendanceTimelineRef}
                          onMouseMove={handleAttendanceTimelineMouseMove}
                          onMouseLeave={handleAttendanceTimelineMouseLeave}
                          onClick={handleAttendanceTimelineClick}
                          sx={{
                            position: 'relative',
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              height: 4,
                              bgcolor: 'rgba(255,255,255,0.22)',
                              borderRadius: 2,
                              overflow: 'visible',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${videoDuration > 0 ? (videoCurrentTime / videoDuration) * 100 : 0}%`,
                                bgcolor: 'primary.main',
                                borderRadius: 2,
                              }}
                            />
                            {videoDuration > 0 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: `${(videoCurrentTime / videoDuration) * 100}%`,
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: 13,
                                  height: 13,
                                  borderRadius: '50%',
                                  bgcolor: '#fff',
                                  boxShadow: '0 1px 5px rgba(0,0,0,0.55)',
                                  zIndex: 4,
                                  pointerEvents: 'none',
                                }}
                              />
                            )}
                            {showAttendanceTimeline &&
                              videoDuration > 0 &&
                              attendanceLogs.filter((log) => !hiddenActions.has(log.action)).map((log) => {
                                const leftPct = Math.max(0, Math.min(100, (log.offset_seconds / videoDuration) * 100));
                                return (
                                  <Box
                                    key={log.attendance_id}
                                    sx={{
                                      position: 'absolute',
                                      left: `${leftPct}%`,
                                      top: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      width: 11,
                                      height: 11,
                                      borderRadius: '50%',
                                      bgcolor: log.action === 'check_in' ? '#4caf50' : '#f44336',
                                      border: '2px solid rgba(255,255,255,0.92)',
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.55)',
                                      zIndex: 3,
                                      pointerEvents: 'none',
                                    }}
                                  />
                                );
                              })}
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={handlePlayPause}
                          sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                        >
                          {isPlaying ? <PauseIcon sx={{ fontSize: 22 }} /> : <PlayArrowIcon sx={{ fontSize: 22 }} />}
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={handleSkipBackward}
                          sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                        >
                          <Replay10Icon sx={{ fontSize: 20 }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={handleSkipForward}
                          sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                        >
                          <Forward10Icon sx={{ fontSize: 20 }} />
                        </IconButton>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#fff',
                            fontFamily: scrubberShowWallClock ? 'inherit' : 'monospace',
                            fontSize: '0.77rem',
                            letterSpacing: scrubberShowWallClock ? 0.15 : 0.4,
                            ml: 0.5,
                          }}
                        >
                          {scrubberShowWallClock
                            ? `${scrubberWallCurrent} / ${scrubberWallEnd}`
                            : `${formatVideoTime(videoCurrentTime, {
                                useExtendedFormat: (videoDuration || 0) >= VIDEO_TIME_ONE_HOUR_SEC,
                              })} / ${formatVideoTime(videoDuration || 0, {
                                useExtendedFormat: (videoDuration || 0) >= VIDEO_TIME_ONE_HOUR_SEC,
                              })}`}
                        </Typography>

                        <Box sx={{ flex: 1 }} />

                        {playbackSpeed !== 1 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.8)',
                              fontFamily: 'monospace',
                              fontSize: '0.72rem',
                              bgcolor: 'rgba(255,255,255,0.15)',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.75,
                              mr: 0.5,
                            }}
                          >
                            {playbackSpeed}x
                          </Typography>
                        )}

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePlaybackMute();
                          }}
                          aria-label={playbackMuted ? 'Unmute recording' : 'Mute recording'}
                          sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                        >
                          {playbackMuted ? (
                            <VolumeOffIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <VolumeUpIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={handleFullscreen}
                          sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                        >
                          <FullscreenIcon sx={{ fontSize: 20 }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDotsMenuAnchor(e.currentTarget);
                            setSpeedSubmenuOpen(false);
                          }}
                          sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                        >
                          <MoreVertIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Menu
                      anchorEl={dotsMenuAnchor}
                      open={Boolean(dotsMenuAnchor)}
                      onClose={() => {
                        setDotsMenuAnchor(null);
                        setSpeedSubmenuOpen(false);
                      }}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      slotProps={{
                        paper: {
                          sx: {
                            bgcolor: 'rgba(28,28,28,0.97)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 2,
                            minWidth: 200,
                            color: '#fff',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                          },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSpeedSubmenuOpen((v) => !v);
                        }}
                        sx={{ gap: 1.5, py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
                      >
                        <SpeedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                            Playback speed
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                            Currently {playbackSpeed}x
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          {speedSubmenuOpen ? '▲' : '▼'}
                        </Typography>
                      </MenuItem>

                      {speedSubmenuOpen && (
                        <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((s) => (
                            <Box
                              key={s}
                              onClick={() => handleSpeedChange(s)}
                              sx={{
                                px: 1.25,
                                py: 0.4,
                                borderRadius: 1,
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: playbackSpeed === s ? 700 : 400,
                                bgcolor: playbackSpeed === s ? 'primary.main' : 'rgba(255,255,255,0.1)',
                                color: playbackSpeed === s ? '#fff' : 'rgba(255,255,255,0.75)',
                                border: '1.5px solid',
                                borderColor: playbackSpeed === s ? 'primary.main' : 'transparent',
                                transition: 'all 0.12s',
                                '&:hover': { bgcolor: playbackSpeed === s ? 'primary.dark' : 'rgba(255,255,255,0.18)' },
                              }}
                            >
                              {s === 1 ? 'Normal' : `${s}x`}
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />

                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setExportPanelOpen(true);
                          setDotsMenuAnchor(null);
                          setSpeedSubmenuOpen(false);
                        }}
                        sx={{ gap: 1.5, py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
                      >
                        <DownloadIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                            Download
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                            Export clip as MKV
                          </Typography>
                        </Box>
                      </MenuItem>

                    </Menu>

                    {previewVisible && showAttendanceTimeline && hoveredAttendanceLog && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 82,
                          left: `clamp(8px, calc(${previewXPercent * 100}% - 140px), calc(100% - 288px))`,
                          zIndex: 10,
                          width: 280,
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          boxShadow: '0 6px 28px rgba(0,0,0,0.6)',
                          bgcolor: 'rgba(18,18,18,0.96)',
                          backdropFilter: 'blur(6px)',
                          pointerEvents: 'none',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -6,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            borderLeft: '7px solid transparent',
                            borderRight: '7px solid transparent',
                            borderTop: '7px solid rgba(18,18,18,0.96)',
                          },
                        }}
                      >
                        {attendancePreviewStatus === 'ready' && attendanceLogPreviewImage ? (
                          <Box
                            component="img"
                            src={attendanceLogPreviewImage}
                            alt=""
                            sx={{ width: '100%', display: 'block', maxHeight: 200, objectFit: 'cover' }}
                          />
                        ) : (
                          <Box sx={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {attendancePreviewStatus === 'missing' ? (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                No image
                              </Typography>
                            ) : (
                              <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.6)' }} />
                            )}
                          </Box>
                        )}
                        <Box sx={{ px: 1.5, py: 0.75 }}>
                          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, display: 'block' }}>
                            {hoveredAttendanceLog.person_name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                            {hoveredAttendanceLog.action === 'check_in' ? 'Check In' : 'Check Out'}
                            {' · '}
                            {typeof hoveredAttendanceLog.timestamp === 'string' &&
                            hoveredAttendanceLog.timestamp.trim()
                              ? formatRecordedDate(hoveredAttendanceLog.timestamp)
                              : formatWallClockFromRecordingOffset(
                                  recordingStartIsoForWallClock,
                                  hoveredAttendanceLog.offset_seconds
                                ) ||
                                formatVideoTime(hoveredAttendanceLog.offset_seconds, {
                                  useExtendedFormat: (videoDuration || 0) >= VIDEO_TIME_ONE_HOUR_SEC,
                                })}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
              </Box>

              {!videoLoading &&
                !videoError &&
                exportPanelOpen &&
                (videoObjectUrl || (useHlsPlayback && hlsManifestAbsoluteUrl)) && (
                  <RecordingDownloadPanel
                    durationSeconds={
                      Number.isFinite(videoDuration) && videoDuration > 0
                        ? videoDuration
                        : Number(selectedRecording?.duration_seconds) || 0
                    }
                    currentTime={videoCurrentTime}
                    startSeconds={clipStartSeconds}
                    endSeconds={clipEndSeconds}
                    recordingStartIso={recordingStartIsoForWallClock}
                    onStartChange={setClipStartSeconds}
                    onEndChange={setClipEndSeconds}
                    onExport={handleExportClip}
                    onClose={() => setExportPanelOpen(false)}
                    disabled={videoLoading || Boolean(videoError)}
                  />
                )}
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

export default RecordingPlaybackModal;
